<template>
  <div class="card">
    <div class="card-body">
      <div
        class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3"
      >
        <h4 class="font-serif mb-0">Journal entries</h4>
        <div class="d-flex flex-wrap align-items-center gap-3 fs-9">
          <button
            v-if="filters.invoiceId !== undefined"
            type="button"
            class="btn btn-sm btn-outline-primary"
            data-test="clear-invoice"
            @click="$emit('change:filters', { invoiceId: undefined })"
          >
            {{ invoiceFilterLabel }}
            <FontAwesomeIcon icon="times" class="ms-1" />
          </button>
          <select
            class="form-select form-select-sm w-auto"
            data-test="kind-filter"
            :value="filters.kind ?? ALL"
            @change="onKindChange"
          >
            <option :value="ALL">All kinds</option>
            <option v-for="kind in kinds" :key="kind" :value="kind">
              {{ humanize(kind) }}
            </option>
          </select>
          <select
            class="form-select form-select-sm w-auto"
            data-test="state-filter"
            :value="filters.state ?? ALL"
            @change="onStateChange"
          >
            <option :value="ALL">All states</option>
            <option v-for="state in FILTER_STATES" :key="state" :value="state">
              {{ STATE_LABELS[state] }}
            </option>
          </select>
          <span class="text-body-secondary text-nowrap"
            >− owed to company · + owed to Soldera</span
          >
        </div>
      </div>

      <div class="table-responsive">
        <table class="table table-sm table-hover mb-0 align-middle">
          <thead>
            <tr>
              <th>Entry</th>
              <th>Date</th>
              <th>Kind</th>
              <th>Description</th>
              <th class="text-end text-nowrap">Company balance</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="entry in visibleEntries" :key="entry.id">
              <tr
                :data-entry-id="entry.id"
                class="cursor-pointer"
                @click="toggle(entry.id)"
              >
                <td
                  :class="entryCellClass(entry, 'start')"
                  class="font-monospace text-nowrap"
                >
                  #{{ entry.id }}
                </td>
                <td
                  :class="entryCellClass(entry, 'middle')"
                  class="text-body-secondary text-nowrap"
                >
                  {{ formatOccurredAt(entry.occurred_at) }}
                </td>
                <td
                  :class="entryCellClass(entry, 'middle')"
                  class="text-nowrap"
                >
                  <span
                    class="badge badge-phoenix badge-phoenix-secondary"
                    :title="entry.kind"
                    data-test="entry-kind"
                    >{{ humanize(entry.kind) }}</span
                  >
                </td>
                <td :class="entryCellClass(entry, 'middle')" class="w-100">
                  <span
                    :title="entry.description"
                    data-test="entry-description"
                    >{{ entryDescription(entry.description) }}</span
                  >
                  <span
                    v-if="entry.invoice_links.length"
                    class="text-body-secondary text-nowrap ms-2"
                    >{{ invoiceNumbers(entry) }}</span
                  >
                </td>
                <td
                  :class="entryCellClass(entry, 'middle')"
                  class="text-end fw-semibold text-nowrap tabular-amount"
                >
                  {{ formatSignedAmount(entry.company_balance) }}
                  <span class="text-body-secondary fw-normal">{{
                    entry.currency
                  }}</span>
                </td>
                <td :class="entryCellClass(entry, 'end')" class="text-nowrap">
                  <span
                    v-if="entry.state !== 'none'"
                    :class="STATE_CLASSES[entry.state]"
                    data-test="entry-state"
                    >{{ STATE_LABELS[entry.state] }}</span
                  >
                </td>
              </tr>
              <tr v-if="entry.id === filters.expandedEntryId">
                <td colspan="6" class="p-3 border border-primary border-top-0">
                  <LedgerEntryDetail
                    :entry
                    :sources="sourcesByEntry[entry.id]"
                    @go:entry="goToEntry"
                  />
                </td>
              </tr>
            </template>
            <tr v-if="visibleEntries.length === 0">
              <td colspan="6" class="text-body-secondary">0 entries</td>
            </tr>
            <tr
              v-for="(total, index) in totals"
              :key="total.currency"
              class="fw-semibold"
            >
              <td colspan="4" class="fw-normal text-body-secondary">
                <template v-if="index === 0">{{ entryCountLabel }}</template>
              </td>
              <td class="text-end text-nowrap tabular-amount" data-test="total">
                {{ formatSignedAmount(total.amount) }} {{ total.currency }}
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { formatDate } from "@/composables/formatUtils";
import LedgerEntryDetail from "./LedgerEntryDetail.vue";
import { ledgerApi } from "./ledgerApi";
import {
  entryDescription,
  formatSignedAmount,
  humanize,
  sumByCurrency,
} from "./ledgerFormat";
import type {
  LedgerEntry,
  LedgerEntryFilters,
  LedgerEntryState,
  LedgerSourceCard,
} from "./ledgerTypes";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const props = defineProps<{
  entries: LedgerEntry[];
  filters: LedgerEntryFilters;
}>();

const emit = defineEmits<{
  "change:filters": [patch: Partial<LedgerEntryFilters>];
}>();

const ALL = "ALL";
const FILTER_STATES: LedgerEntryState[] = [
  "open",
  "held",
  "settled",
  "reversed",
  "none",
];
const STATE_LABELS: Record<LedgerEntryState, string> = {
  open: "Open",
  held: "Held back",
  settled: "Settled",
  reversed: "Reversed",
  none: "No obligation",
};
// Design-system status semantics: only a held entry asks for action and gets the attention pill; a
// reversed one is consumed and gets the inactive pill.
const STATE_CLASSES: Record<LedgerEntryState, string> = {
  open: "fw-semibold",
  held: "badge pill-attention",
  settled: "text-body-secondary",
  reversed: "badge pill-inactive",
  none: "",
};

const { globalErrorHandler } = useGlobalHandlers();
const sourcesByEntry = ref<Record<number, LedgerSourceCard[]>>({});

const kinds = computed(() =>
  [...new Set(props.entries.map((entry) => entry.kind))].sort()
);

const visibleEntries = computed(() =>
  props.entries.filter(
    (entry) =>
      (props.filters.kind === undefined || entry.kind === props.filters.kind) &&
      (props.filters.state === undefined ||
        entry.state === props.filters.state) &&
      (props.filters.invoiceId === undefined ||
        entry.invoice_links.some(
          (link) => link.invoice_id === props.filters.invoiceId
        ))
  )
);

const totals = computed(() =>
  sumByCurrency(
    visibleEntries.value.map((entry) => ({
      currency: entry.currency,
      amount: entry.company_balance,
    }))
  )
);

const entryCountLabel = computed(
  () =>
    `${visibleEntries.value.length} ${visibleEntries.value.length === 1 ? "entry" : "entries"}`
);

const invoiceFilterLabel = computed(() => {
  const link = props.entries
    .flatMap((entry) => entry.invoice_links)
    .find((candidate) => candidate.invoice_id === props.filters.invoiceId);
  return link?.invoice_number ?? `Invoice ${props.filters.invoiceId}`;
});

watch(
  () => props.filters.expandedEntryId,
  (entryId) => {
    if (entryId !== undefined) loadSources(entryId);
  },
  { immediate: true }
);

// A function declaration, not a const: the immediate watcher above calls it before this line runs.
async function loadSources(entryId: number) {
  const entry = props.entries.find((candidate) => candidate.id === entryId);
  if (!entry?.has_sources || sourcesByEntry.value[entryId]) return;
  try {
    const response = await ledgerApi.getEntrySources(entryId);
    sourcesByEntry.value = {
      ...sourcesByEntry.value,
      [entryId]: response.data.sources,
    };
  } catch (error) {
    globalErrorHandler(error);
  }
}

// The theme colours every table cell directly, so muting a row has to be set on its cells. Borders on
// a collapsed-border table also only render on cells, so the expanded entry row and its detail row
// are joined into one outlined block cell by cell.
const entryCellClass = (
  entry: LedgerEntry,
  position: "start" | "middle" | "end"
) => {
  const expanded = entry.id === props.filters.expandedEntryId;
  return {
    "text-body-tertiary": isMuted(entry),
    "border-top": expanded,
    "border-primary": expanded,
    "border-start": expanded && position === "start",
    "border-end": expanded && position === "end",
  };
};

const isMuted = (entry: LedgerEntry) =>
  entry.state === "reversed" || entry.kind === "REVERSAL";

const invoiceNumbers = (entry: LedgerEntry) =>
  [...new Set(entry.invoice_links.map((link) => link.invoice_number))].join(
    " · "
  );

const formatOccurredAt = (occurredAt: string) =>
  formatDate(occurredAt.slice(0, 10));

const toggle = (entryId: number) => {
  emit("change:filters", {
    expandedEntryId:
      props.filters.expandedEntryId === entryId ? undefined : entryId,
  });
};

const goToEntry = (entryId: number) => {
  emit("change:filters", {
    kind: undefined,
    state: undefined,
    invoiceId: undefined,
    expandedEntryId: entryId,
  });
};

const onKindChange = (event: Event) => {
  const value = (event.target as HTMLSelectElement).value;
  emit("change:filters", { kind: value === ALL ? undefined : value });
};

const onStateChange = (event: Event) => {
  const value = (event.target as HTMLSelectElement).value;
  emit("change:filters", {
    state: value === ALL ? undefined : (value as LedgerEntryState),
  });
};
</script>

<style lang="scss" scoped>
.tabular-amount {
  font-variant-numeric: tabular-nums;
}
</style>
